import {
  capabilityDefinition,
  errorMessage,
  narrowestRequestableScope,
  type ApiClient,
  type ApiErrorBody,
  type CapabilityKey,
} from "@planner/core";
import { useEffect, useId, useState, type ReactNode } from "react";
import { useLocale } from "../i18n/provider.js";
import { platform } from "../platform/index.js";

/**
 * **طلبُ صلاحيةٍ من الشاشة التي مُنعت منها.**
 *
 * فشاشةُ المنع تسمّي القدرةَ الناقصة **وتعرض طلبَها بضغطة** — ولم يكن أحدٌ
 * يوصّل تلك الضغطة بشيء: `onRequestAccess` اختياريٌّ **ولا تمرّره شاشةٌ واحدة**،
 * فالزرُّ لا يُرسَم أصلاً. ولا في النظام كلِّه طريقٌ آخر يُنشئ طلبَ صلاحية —
 * فكان `access_requests.create` قدرةً لها مسارٌ ولا بابَ له.
 *
 * **ويُسأل عن السبب لا عن النطاق**: «قسم» و«عملاء مسندون» كلماتٌ لا تعني
 * موظّفاً مُنع من شاشة. فيُطلَب أضيقُ ما تُجيزه القدرة، ومن يبتّ يوسّع إن شاء.
 */

const MIN_REASON = 10;

/** يُرصَد مرّةً، فيُفتَح الحوارُ من أي شاشة بلا أن تعرف عنه شيئاً */
let open: ((capability: CapabilityKey) => void) | null = null;

export function requestAccessFor(capability: CapabilityKey): void {
  open?.(capability);
}

export function RequestAccessHost({ client }: { readonly client: ApiClient }): ReactNode {
  const [capability, setCapability] = useState<CapabilityKey | null>(null);

  useEffect(() => {
    open = setCapability;
    return () => {
      open = null;
    };
  }, []);

  if (capability === null) return null;
  return (
    <RequestDialog
      client={client}
      capability={capability}
      onClose={() => {
        setCapability(null);
      }}
    />
  );
}

function RequestDialog({
  client,
  capability,
  onClose,
}: {
  readonly client: ApiClient;
  readonly capability: CapabilityKey;
  readonly onClose: () => void;
}): ReactNode {
  const { t, locale } = useLocale();
  const reasonId = useId();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiErrorBody | null>(null);
  const [sent, setSent] = useState(false);

  async function submit(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    const result = await client.request("/access-requests", {
      method: "POST",
      idempotencyKey: platform.newId(),
      body: {
        capability,
        scope: narrowestRequestableScope(capability),
        reason,
      },
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setSent(true);
  }

  return (
    <div className="mod" role="dialog" aria-modal="true" aria-label={t("state.forbidden.request")}>
      <div className="mod-head">
        <b>{t("state.forbidden.request")}</b>
        <button
          data-close
          type="button"
          className="icb"
          aria-label={t("common.close")}
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <div className="confirm-body">
        {sent ? (
          <>
            {/** **والوصولُ يُقال** — فلا يُترَك الطالبُ يسأل: وصل ولا لأ؟ */}
            <p className="confirm-what" role="status">
              {t("state.forbidden.requested")}
            </p>
            <div className="mod-actions">
              <button type="button" className="btn" onClick={onClose}>
                {t("common.close")}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="confirm-what">{capabilityDefinition(capability).label[locale]}</p>
            <p className="field-hint">{capabilityDefinition(capability).description[locale]}</p>

            {error ? (
              <div className="signin-error" role="alert">
                <p>{errorMessage(locale, error)}</p>
                <div className="codes">
                  <span className="code">{error.code}</span>
                  <span className="code">{error.correlationId}</span>
                </div>
              </div>
            ) : null}

            <div className="field">
              <label htmlFor={reasonId}>{t("access.reason")}</label>
              <textarea
                id={reasonId}
                rows={3}
                value={reason}
                onChange={(event) => {
                  setReason(event.target.value);
                }}
              />
              {/** **والحدُّ يُقال قبل الضغط** — لا بعد رفضٍ من السيرفر */}
              <p className="field-hint">{t("access.reasonHint")}</p>
            </div>

            <div className="mod-actions">
              <button type="button" className="btn gh" onClick={onClose} disabled={busy}>
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="btn"
                disabled={busy || reason.trim().length < MIN_REASON}
                onClick={() => {
                  void submit();
                }}
              >
                {t(busy ? "common.working" : "state.forbidden.request")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
